import React, { useState, forwardRef } from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql, Link } from 'gatsby';
import { motion, AnimatePresence } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { SingleColumnCarousel, Button } from 'components';
import { slugify } from 'utils';

const convertToSeconds = (timestamp) => {
  const date = new Date(timestamp);
  const secondsSinceEpoch = date.getTime() / 1000;
  return secondsSinceEpoch;
};

const ProductSpotlights = forwardRef((props, ref) => {
  const { showcaseSpotlights, doorSpotlights, doorPartSpotlights, stairPartSpotlights } =
    useStaticQuery(graphql`
      query {
        doorSpotlights: allSanityDoor(filter: { spotlight: { eq: true } }) {
          nodes {
            _type
            _createdAt
            id
            title
            description
            productVariants {
              default {
                productId
              }
            }
            images {
              mainImage {
                hotspot {
                  height
                  width
                  x
                  y
                }
                crop {
                  top
                  bottom
                  left
                  right
                }
                asset {
                  _id
                  originalFilename
                  url
                  metadata {
                    dimensions {
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
        doorPartSpotlights: allSanityDoorPart(filter: { spotlight: { eq: true } }) {
          nodes {
            _type
            _createdAt
            id
            title
            description
            productVariants {
              default {
                productId
              }
            }
            images {
              mainImage {
                hotspot {
                  height
                  width
                  x
                  y
                }
                crop {
                  top
                  bottom
                  left
                  right
                }
                asset {
                  _id
                  originalFilename
                  url
                  metadata {
                    dimensions {
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
        stairPartSpotlights: allSanityStairPart(filter: { spotlight: { eq: true } }) {
          nodes {
            _type
            _createdAt
            id
            title
            description
            productVariants {
              default {
                productId
              }
            }
            images {
              mainImage {
                hotspot {
                  height
                  width
                  x
                  y
                }
                crop {
                  top
                  bottom
                  left
                  right
                }
                asset {
                  _id
                  originalFilename
                  url
                  metadata {
                    dimensions {
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
        showcaseSpotlights: allSanityShowcase(filter: { spotlight: { eq: true } }) {
          nodes {
            _type
            _createdAt
            id
            title
            _rawBody
            images {
              mainImage {
                hotspot {
                  height
                  width
                  x
                  y
                }
                crop {
                  top
                  bottom
                  left
                  right
                }
                asset {
                  _id
                  originalFilename
                  url
                  metadata {
                    dimensions {
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
      }
    `);

  const spotlights = [
    ...showcaseSpotlights.nodes,
    ...doorSpotlights.nodes,
    ...doorPartSpotlights.nodes,
    ...stairPartSpotlights.nodes
  ];

  if (!spotlights.length) {
    return;
  }

  const sortedSpotlights = spotlights.sort(
    (a, b) => convertToSeconds(b._createdAt) - convertToSeconds(a._createdAt)
  );

  const [currentSpotlightIndex, setCurrentSpotlightIndex] = useState(0);

  const images = sortedSpotlights.map((spotlight) => spotlight.images.mainImage);

  return (
    <Container className={props.className} ref={ref}>
      <Text className="fade-in">
        <h3>
          Product Spotlights
          <br />& Current Trends
        </h3>
        {/* SEO: Always render all spotlights' static Headline/Body, visually hidden except for the visible one */}
        {sortedSpotlights.map((spotlight, i) => {
          const spotlightType = spotlight._type;

          if (spotlightType === 'showcase') {
            const slug = `${slugify(spotlight.title)}`;
            spotlight.path = `/showcases/${slug}`;
          } else {
            let basePath;

            if (spotlightType === 'door') {
              basePath = '/doors/';
            } else if (spotlightType === 'doorPart' || spotlightType === 'stairPart') {
              basePath = '/stair-and-door-parts/';
            }

            const slug = `${slugify(spotlight.title, {
              remove: /[*+~.,()'"!:@/]/g,
              lower: true,
              trim: true
            })}-${spotlight.productVariants.default.productId.toLowerCase()}`;

            spotlight.path = `${basePath}${slug}`;
          }

          const isVisible = currentSpotlightIndex === i;

          return (
            <div
              key={i}
              aria-hidden={!isVisible}
              style={{
                display: 'block',
                position: isVisible ? 'static' : 'absolute',
                left: isVisible ? 'auto' : '-9999px',
                width: '100%',
                height: isVisible ? 'auto' : 0,
                overflow: isVisible ? 'visible' : 'hidden',
                top: 0
              }}>
              {/* Visually hidden static content for SEO (always in DOM) */}
              {!isVisible && (
                <>
                  <h4>{spotlight.title}</h4>
                  <div>
                    {spotlight._rawBody ? (
                      <PortableText value={spotlight._rawBody} />
                    ) : (
                      <p style={{ marginBottom: 0 }}>{spotlight.description}</p>
                    )}
                  </div>
                  <Button to={spotlight.path}>Read More</Button>
                </>
              )}
            </div>
          );
        })}

        {/* Animated Headline & Body for the currently visible spotlight only */}
        <AnimatePresence mode="wait" initial={false}>
          {sortedSpotlights.length > 0 && (
            <React.Fragment key={currentSpotlightIndex}>
              <Headline>{sortedSpotlights[currentSpotlightIndex].title}</Headline>
              <Body>
                {sortedSpotlights[currentSpotlightIndex]._rawBody ? (
                  <PortableText value={sortedSpotlights[currentSpotlightIndex]._rawBody} />
                ) : (
                  <p style={{ marginBottom: 0 }}>
                    {sortedSpotlights[currentSpotlightIndex].description}
                  </p>
                )}
              </Body>
              <Button to={sortedSpotlights[currentSpotlightIndex].path}>Read More</Button>
            </React.Fragment>
          )}
        </AnimatePresence>
      </Text>
      <SingleColumnCarousel
        images={images}
        imageWidth={590}
        imageHeight={590}
        setCurrentSpotlightIndex={setCurrentSpotlightIndex}
        showIndicators
      />
    </Container>
  );
});

const Container = styled.div`
  display: grid;
  grid-column: 1/-1;
  grid-row: 2;
  grid-template-columns: subgrid;
  grid-template-rows: auto auto;
  margin: 100px 0 39px;
  place-items: start center;

  @media (width >= 850px) {
    grid-template-rows: initial;
  }

  @media (width >= 1200px) {
    align-items: center;
  }

  /* Carousel */
  > :nth-child(2) {
    grid-column: 1/-1;
    grid-row: 1/2;
    max-width: 470px;
    width: 100%;

    @media (width >= 850px) {
      max-width: 590px;
      grid-column: 8/-1;
      width: calc(100% + 20px);
    }

    @media (width < 850px) {
      /* Carousel Controls */
      > div:last-of-type {
        margin-bottom: 38px;
      }
    }
  }
`;

const Text = styled.div`
  grid-column: 1/-1;
  max-width: 470px;

  @media (width >= 850px) {
    grid-column: 2/7;
  }

  @media (width >= 1060px) {
    grid-column: 3/7;
  }

  @media (width >= 1200px) {
    align-items: center;
    position: relative;
    top: -24px;
  }

  h3 {
    font-size: 2rem;
    margin-bottom: 1.075em;
  }

  a {
    display: inline-block;
    margin-top: 2rem;
  }
`;

const Headline = styled(motion.h4).attrs({
  initial: { opacity: 0, x: 140 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -140 },
  transition: { duration: 0.375 }
})`
  font-size: 1.125rem;
  margin-bottom: 1.25em;
`;

const Body = styled(motion.div).attrs({
  initial: { opacity: 0, x: 140 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -140 },
  transition: { duration: 0.375 }
})`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 7;
  display: -webkit-box;
  overflow: hidden;

  ul,
  ol {
    list-style-position: inside;
  }

  h1,
  p,
  a,
  ul,
  ol {
    margin-bottom: 1.25rem;
  }

  a,
  a:visited,
  a:hover,
  a:active {
    color: var(--connolly-black);
  }
`;

export default ProductSpotlights;
